import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { MenuIcon, XIcon } from "@heroicons/react/outline";

const navigation = [
  {
    name: "Falcon",
    href: "#",
    submenu: [
      { name: "Boosters", href: "/falcon/boosters/" },
      { name: "Compare", href: "/falcon/compare/" },
      { name: "Stats", href: "/falcon/stats/" },
    ],
  },
  {
    name: "Dragon",
    href: "#",
    submenu: [
      { name: "Dragons", href: "/dragon/spacecraft/" },
      { name: "Stats", href: "/dragon/stats/" },
    ],
  },
  {
    name: "Starship",
    href: "#",
    submenu: [
      { name: "Boosters", href: "/starship/boosters/" },
      { name: "Ships", href: "/starship/ships/" },
      { name: "Stats", href: "/starship/stats/" },
    ],
  },
  { name: "Launches", href: "/launches/" },
  {
    name: "More",
    href: "#",
    submenu: [
      { name: "Calendar", href: "/launch-calendar/" },
      { name: "Pads", href: "/pads/" },
      { name: "Recovery Zones", href: "/recoveryzones/" },
      { name: "Mass to Orbit", href: "/graphs/" },
    ],
  },
];

const classNames = (...classes) => classes.filter(Boolean).join(" ");

function Navbar() {
  const location = useLocation();
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const closeTimerRef = useRef(null);

  useEffect(() => {
    const shell = document.querySelector(".bt-nav-shell");
    if (!shell) return undefined;

    const mobile = window.matchMedia("(max-width: 768px)");
    let lastY = window.scrollY;
    let away = false;

    const setAway = (next) => {
      if (away === next) return;
      away = next;
      shell.classList.toggle("is-away", next);
    };

    const onScroll = () => {
      const y = Math.max(0, window.scrollY);
      const delta = y - lastY;
      if (!mobile.matches || mobileOpen || y < 8) {
        setAway(false);
      } else if (delta > 6) {
        setAway(true);
      } else if (delta < -4) {
        setAway(false);
      }
      lastY = y;
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    mobile.addEventListener("change", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      mobile.removeEventListener("change", onScroll);
      shell.classList.remove("is-away");
    };
  }, [mobileOpen]);

  const isCurrent = (item) => {
    if (item.href !== "#" && location.pathname.startsWith(item.href)) {
      return true;
    }
    return item.submenu?.some((subItem) => location.pathname.startsWith(subItem.href));
  };

  const closeMenus = () => {
    window.clearTimeout(closeTimerRef.current);
    setOpenMenu(null);
    setMobileOpen(false);
  };

  const openDesktopMenu = (name) => {
    window.clearTimeout(closeTimerRef.current);
    setOpenMenu(name);
  };

  const queueDesktopMenuClose = () => {
    window.clearTimeout(closeTimerRef.current);
    closeTimerRef.current = window.setTimeout(() => setOpenMenu(null), 160);
  };

  return (
    <header className="bt-nav-shell">
      <nav className="bt-nav" aria-label="Main navigation">
        <Link to="/" className="bt-brand" onClick={closeMenus}>
          <img className="bt-brand-logo" src="/booster-tracker.png" alt="" aria-hidden="true" />
          <span>
            <strong>Booster Tracker</strong>
          </span>
        </Link>

        <div className="bt-nav-links">
          {navigation.map((item) => (
            <div
              className="bt-nav-item"
              key={item.name}
              onMouseEnter={() => openDesktopMenu(item.name)}
              onMouseLeave={queueDesktopMenuClose}
            >
              {item.submenu ? (
                <button
                  type="button"
                  className={classNames("bt-nav-link", isCurrent(item) && "active")}
                  onClick={() => setOpenMenu(openMenu === item.name ? null : item.name)}
                >
                  {item.name}
                  <span className="bt-nav-caret" aria-hidden="true" />
                </button>
              ) : (
                <Link to={item.href} className={classNames("bt-nav-link", isCurrent(item) && "active")}>
                  {item.name}
                </Link>
              )}

              {item.submenu && openMenu === item.name && (
                <div className="bt-nav-menu">
                  {item.submenu.map((subItem) => (
                    <Link
                      key={subItem.href}
                      to={subItem.href}
                      className={classNames("bt-nav-sub-link", location.pathname.startsWith(subItem.href) && "active")}
                      onClick={closeMenus}
                    >
                      {subItem.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="bt-nav-actions">
          <button type="button" className="bt-mobile-toggle" onClick={() => setMobileOpen((open) => !open)}>
            <span className="sr-only">Toggle navigation</span>
            {mobileOpen ? <XIcon className="bt-mobile-icon" /> : <MenuIcon className="bt-mobile-icon" />}
          </button>
        </div>
      </nav>

      {mobileOpen && (
        <div className="bt-mobile-menu">
          {navigation.map((item) => (
            <div className="bt-mobile-group" key={item.name}>
              {item.submenu ? (
                <>
                  <div className={classNames("bt-mobile-heading", isCurrent(item) && "active")}>{item.name}</div>
                  {item.submenu.map((subItem) => (
                    <Link
                      key={subItem.href}
                      to={subItem.href}
                      className={classNames("bt-mobile-link", location.pathname.startsWith(subItem.href) && "active")}
                      onClick={closeMenus}
                    >
                      {subItem.name}
                    </Link>
                  ))}
                </>
              ) : (
                <Link
                  to={item.href}
                  className={classNames("bt-mobile-heading", isCurrent(item) && "active")}
                  onClick={closeMenus}
                >
                  {item.name}
                </Link>
              )}
            </div>
          ))}
        </div>
      )}
    </header>
  );
}

export default Navbar;
